import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Select,
  Text,
  Popover,
  Alert,
  Checkbox,
  Paper,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconPaperclip, IconDeviceFloppy } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import TextEditor from "./textEditor";
import EmailPlaceholderLegend from "./emailPlaceholderLegend";
import EntityEmailStoredFiles from "./entityEmailStoredFiles";
import { documentsForEntity } from "../data/entityEmailDocuments";
import {
  getEntityDocumentEmailConfig,
  isOptionalDocumentAllowed,
} from "../data/entityDocumentEmail";
import {
  GI_DOCUMENT_EMAIL_PLACEHOLDERS,
  GI_DOCUMENT_PASSWORD_PLACEHOLDERS,
} from "../data/emailPlaceholders";
import {
  fetchEmailTemplatesForTypes,
  invalidateEmailTemplateListCache,
} from "../helpers/emailTemplateListLoader";

const BUILTIN_TEMPLATE = "__builtin__";
const EMAIL_RE = /\S+@\S+\.\S+/;

function isValidEmailList(value, { required = false } = {}) {
  const parts = String(value || "")
    .split(/[,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (!parts.length) return !required;
  return parts.every((part) => EMAIL_RE.test(part));
}

function uniqueTemplates(docs) {
  const byName = new Map();
  docs.forEach((doc) => {
    const key = `${doc.type || ""}|${String(doc.name || "")
      .trim()
      .toLowerCase()}`;
    const prev = byName.get(key);
    if (!prev) {
      byName.set(key, doc);
      return;
    }
    const newer =
      new Date(doc.updatedAt || doc.createdAt || 0) >=
      new Date(prev.updatedAt || prev.createdAt || 0)
        ? doc
        : prev;
    byName.set(key, newer);
  });
  return Array.from(byName.values());
}

function formatSentAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function defaultAttachments(config, values) {
  return (config.selectableKeys || []).map((key) => {
    const meta = documentsForEntity(config.entity).find((doc) => doc.value === key);
    return {
      key,
      label: meta?.label || key,
      filename: "",
      required: false,
      allowed: isOptionalDocumentAllowed(config.entity, values, key),
    };
  });
}

function appendCustomAttachments(current, customDocs) {
  const keys = new Set((current || []).map((item) => item.key));
  const extra = (customDocs || [])
    .filter((doc) => doc?.active !== false && doc?.template?.isUploaded)
    .map((doc) => ({
      key: doc.attachmentKey || `custom:${doc._id}`,
      label: doc.name,
      filename: "",
      required: false,
      allowed: true,
    }))
    .filter((item) => item.key && !keys.has(item.key));
  return [...(current || []), ...extra];
}

export default function SendEntityDocumentEmailModal({
  opened,
  onClose,
  form: entityForm,
  entity,
}) {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const config = getEntityDocumentEmailConfig(entity);
  const [templates, setTemplates] = useState([]);
  const [passwordTemplates, setPasswordTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savePasswordOpen, setSavePasswordOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [passwordTemplateName, setPasswordTemplateName] = useState("");
  const [bodyKey, setBodyKey] = useState(0);
  const [passwordBodyKey, setPasswordBodyKey] = useState(0);
  const [activeField, setActiveField] = useState("body");
  const [attachments, setAttachments] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [entityFiles, setEntityFiles] = useState([]);
  const [selectedEntityFileIds, setSelectedEntityFileIds] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [preview, setPreview] = useState(null);
  const [recipientOptions, setRecipientOptions] = useState([]);

  const entityId = entityForm?.values?._id;
  const emailApi = api?.[config?.apiKey];

  const form = useForm({
    initialValues: {
      templateId: BUILTIN_TEMPLATE,
      passwordTemplateId: BUILTIN_TEMPLATE,
      to: "",
      cc: "",
      bcc: "",
      subject: config?.defaultSubject || "",
      body: config?.defaultBody || "",
      encrypt: false,
      passwordSubject: config?.defaultPasswordSubject || "",
      passwordBody: config?.defaultPasswordBody || "",
    },
    validate: {
      to: (value) =>
        isValidEmailList(value, { required: true })
          ? null
          : t("Enter a valid email address"),
      cc: (value) =>
        isValidEmailList(value) ? null : t("Enter a valid CC email address"),
      bcc: (value) =>
        isValidEmailList(value) ? null : t("Enter a valid BCC email address"),
      subject: (value) =>
        !value || !String(value).trim() ? t("Subject is required") : null,
    },
  });

  const applyDocumentTemplate = useCallback(
    (tpl) => {
      form.setFieldValue("templateId", tpl?._id || BUILTIN_TEMPLATE);
      form.setFieldValue("subject", tpl?.subject || config.defaultSubject);
      form.setFieldValue("body", tpl?.body || config.defaultBody);
      form.setFieldValue("cc", tpl?.cc || "");
      form.setFieldValue("bcc", tpl?.bcc || "");
      setBodyKey((key) => key + 1);
      setPreview(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config]
  );

  const applyPasswordTemplate = useCallback(
    (tpl) => {
      form.setFieldValue("passwordTemplateId", tpl?._id || BUILTIN_TEMPLATE);
      form.setFieldValue(
        "passwordSubject",
        tpl?.subject || config.defaultPasswordSubject
      );
      form.setFieldValue(
        "passwordBody",
        tpl?.body || config.defaultPasswordBody
      );
      setPasswordBodyKey((key) => key + 1);
      setPreview(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config]
  );

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetchEmailTemplatesForTypes(
        api,
        config.templateType,
        config.passwordTemplateType
      );
      const documentTemplates = uniqueTemplates(res.templates || []);
      const passwordDocs = uniqueTemplates(res.passwordTemplates || []);
      setTemplates(documentTemplates);
      setPasswordTemplates(passwordDocs);
      return { documentTemplates, passwordDocs };
    } catch (error) {
      setTemplates([]);
      setPasswordTemplates([]);
      return { documentTemplates: [], passwordDocs: [] };
    } finally {
      setLoadingTemplates(false);
    }
  }, [api, config]);

  useEffect(() => {
    if (!opened || !entityId || !config) return undefined;
    let cancelled = false;
    form.setFieldValue("encrypt", false);
    form.clearErrors();
    setPreview(null);
    setRecipientOptions([]);
    setSelectedKeys([...(config.defaultKeys || [])]);
    setAttachments(defaultAttachments(config, entityForm?.values));
    setEntityFiles([]);
    setSelectedEntityFileIds([]);
    (async () => {
      const { documentTemplates, passwordDocs } = await loadTemplates();
      if (cancelled) return;
      const documentDefault =
        documentTemplates.find((doc) => doc.isDefault) || documentTemplates[0];
      const passwordDefault =
        passwordDocs.find((doc) => doc.isDefault) || passwordDocs[0];
      applyDocumentTemplate(documentDefault || null);
      applyPasswordTemplate(passwordDefault || null);
      let customDocs = [];
      try {
        const listed = await api.customDocument.list({
          entity: config.entity,
          active: true,
          hasTemplate: true,
        });
        if (listed?.success) customDocs = listed.data?.documents || [];
      } catch (error) {
        customDocs = [];
      }
      if (cancelled) return;
      setAttachments((current) => appendCustomAttachments(current, customDocs));
      try {
        const context = await emailApi.previewEmail(entityId, {
          subject: documentDefault?.subject || config.defaultSubject,
          body: documentDefault?.body || config.defaultBody,
          encrypt: false,
        });
        if (cancelled) return;
        form.setFieldValue("to", context?.suggestedTo || "");
        setRecipientOptions(
          Array.isArray(context?.recipientOptions) ? context.recipientOptions : []
        );
        if (Array.isArray(context?.attachments)) {
          setAttachments(appendCustomAttachments(context.attachments, customDocs));
        }
        if (entity === "debitNote") {
          setEntityFiles(
            Array.isArray(context?.entityFiles) ? context.entityFiles : []
          );
        }
        setEmailLog(Array.isArray(context?.emailLog) ? context.emailLog : []);
      } catch (error) {
        if (!cancelled) {
          setEmailLog([]);
          setRecipientOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, entityId, entity]);

  const insertPlaceholder = (markup) => {
    if (activeField === "subject" || activeField === "passwordSubject") {
      form.setFieldValue(activeField, `${form.values[activeField] || ""}${markup}`);
      return;
    }
    if (activeField === "passwordBody") {
      form.setFieldValue(
        "passwordBody",
        `${form.values.passwordBody || ""}${markup}`
      );
      setPasswordBodyKey((key) => key + 1);
      return;
    }
    form.setFieldValue("body", `${form.values.body || ""}${markup}`);
    setBodyKey((key) => key + 1);
  };

  const payload = () => ({
    to: form.values.to,
    cc: form.values.cc,
    bcc: form.values.bcc,
    subject: form.values.subject,
    body: form.values.body,
    encrypt: form.values.encrypt,
    passwordSubject: form.values.passwordSubject,
    passwordBody: form.values.passwordBody,
    attachmentKeys: selectedKeys,
    ...(entity === "debitNote" ? { entityFileIds: selectedEntityFileIds } : {}),
  });

  const saveTemplate = async ({ name, type, subject, body, cc, bcc, apply }) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    setSavingTemplate(true);
    try {
      const res = await api.add({
        apiEntity: "emailTemplate",
        values: {
          name: trimmed,
          type,
          subject,
          body,
          cc: cc || "",
          bcc: bcc || "",
          isDefault: false,
        },
      });
      if (!res?.success) {
        const message =
          res?.error?.msg || res?.error?.message || res?.message || "";
        showNotification({
          color: "red",
          title: t("Could not save template"),
          message: typeof message === "string" ? message : "",
        });
        return;
      }
      showNotification({ color: "green", title: t("Template saved") });
      invalidateEmailTemplateListCache(
        config.templateType,
        config.passwordTemplateType
      );
      const loaded = await loadTemplates();
      const created = res.data;
      const list =
        type === config.passwordTemplateType
          ? loaded.passwordDocs
          : loaded.documentTemplates;
      const match =
        (created?._id && list.find((doc) => doc._id === created._id)) ||
        list.find((doc) => doc.name === trimmed) ||
        created;
      apply(match || null);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreview = async () => {
    if (!entityId || !emailApi) return;
    setPreviewing(true);
    try {
      const data = await emailApi.previewEmail(entityId, payload());
      setPreview(data);
      if (Array.isArray(data?.attachments)) setAttachments(data.attachments);
      if (entity === "debitNote" && Array.isArray(data?.entityFiles)) {
        const files = data.entityFiles;
        setEntityFiles(files);
        setSelectedEntityFileIds((current) =>
          current.filter((id) => files.some((file) => file.id === id))
        );
      }
      if (Array.isArray(data?.emailLog)) setEmailLog(data.emailLog);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Could not preview email"),
        message: error?.message || "",
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    const valid = form.validate();
    if (valid.hasErrors || !entityId || !emailApi) return;
    const hasStoredFile =
      entity === "debitNote" && selectedEntityFileIds.length > 0;
    if (!selectedKeys.length && !hasStoredFile) {
      showNotification({
        color: "red",
        title: t("Could not send email"),
        message: t("Select at least one document to attach"),
      });
      return;
    }
    setSending(true);
    const notifId = `${entity}-email-${entityId}`;
    showNotification({
      id: notifId,
      loading: true,
      title: t(config.sendingTitle),
      message: t(config.sendingMessage),
      autoClose: false,
    });
    try {
      const data = await emailApi.sendEmail(entityId, payload());
      if (Array.isArray(data?.emailLog)) setEmailLog(data.emailLog);
      updateNotification({
        id: notifId,
        loading: false,
        color: "green",
        title: t(config.sentTitle),
        message: data?.encrypted
          ? t(config.sentWithPassword)
          : t(config.sentPlain),
        autoClose: 4000,
      });
      onClose?.();
    } catch (error) {
      if (Array.isArray(error?.emailLog)) setEmailLog(error.emailLog);
      updateNotification({
        id: notifId,
        loading: false,
        color: "red",
        title: t("Could not send email"),
        message: error?.message || "",
        autoClose: 8000,
      });
    } finally {
      setSending(false);
    }
  };

  const templateOptions = (docs) => [
    { value: BUILTIN_TEMPLATE, label: t("Built-in default") },
    ...docs.map((tpl) => ({
      value: tpl._id,
      label: tpl.isDefault ? `${tpl.name} (${t("Default")})` : tpl.name,
    })),
  ];

  const recentLog = [...emailLog].slice(0, 8);

  if (!config) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(config.title)}
      size="90%"
      styles={{ modal: { maxWidth: 900 } }}
      closeOnClickOutside={!sending}
    >
      <Stack spacing="sm">
        <Select
          label={t("Template")}
          data={templateOptions(templates)}
          value={form.values.templateId}
          onChange={(id) => {
            if (id === BUILTIN_TEMPLATE) {
              applyDocumentTemplate(null);
              return;
            }
            applyDocumentTemplate(templates.find((doc) => doc._id === id) || null);
          }}
          disabled={loadingTemplates || sending}
          searchable
        />
        {recipientOptions.length > 0 && (
          <Select
            label={t("Recipient")}
            placeholder={t("Select to fill To")}
            data={recipientOptions}
            searchable
            value={null}
            onChange={(email) => {
              if (email) form.setFieldValue("to", email);
            }}
            disabled={sending}
            nothingFound={t("No matching address")}
          />
        )}
        <TextInput
          required
          label={t("To")}
          placeholder="name@company.com"
          {...form.getInputProps("to")}
          onFocus={() => setActiveField("to")}
          disabled={sending}
        />
        {recipientOptions.length > 0 && (
          <Select
            label={t("CC recipient")}
            placeholder={t("Select to fill CC")}
            data={recipientOptions}
            searchable
            value={null}
            onChange={(email) => {
              if (email) form.setFieldValue("cc", email);
            }}
            disabled={sending}
            nothingFound={t("No matching address")}
          />
        )}
        <TextInput
          label={t("CC")}
          placeholder={t("Optional")}
          {...form.getInputProps("cc")}
          onFocus={() => setActiveField("cc")}
          disabled={sending}
        />
        <TextInput
          label={t("BCC")}
          placeholder={t("Optional")}
          {...form.getInputProps("bcc")}
          onFocus={() => setActiveField("bcc")}
          disabled={sending}
        />
        <Group align="flex-end" noWrap spacing="sm">
          <TextInput
            required
            style={{ flex: 1 }}
            label={t("Subject")}
            {...form.getInputProps("subject")}
            onFocus={() => setActiveField("subject")}
            disabled={sending}
          />
          <EmailPlaceholderLegend
            tokens={
              activeField === "passwordSubject" || activeField === "passwordBody"
                ? GI_DOCUMENT_PASSWORD_PLACEHOLDERS
                : GI_DOCUMENT_EMAIL_PLACEHOLDERS
            }
            onInsert={insertPlaceholder}
            disabled={sending}
            mb={1}
          />
        </Group>
        <div onFocusCapture={() => setActiveField("body")}>
          <TextEditor
            key={bodyKey}
            form={form}
            name="body"
            label={t("Body")}
          />
          <Text size="xs" color="dimmed" mt={4}>
            {t(
              "You can paste from Word. Tables, headings, lists, and colors are kept."
            )}
          </Text>
        </div>
        <Checkbox
          label={t("Password-protect the PDF")}
          checked={form.values.encrypt}
          onChange={(event) => {
            form.setFieldValue("encrypt", event.currentTarget.checked);
            setPreview(null);
          }}
          disabled={sending}
        />
        <Text size="xs" color="dimmed" mt={-8}>
          {t(
            "A second email with the password is sent to the same recipients. The password is created when you send and is not stored."
          )}{" "}
          {t("Password protection applies to PDFs only.")}
        </Text>
        {form.values.encrypt && (
          <Stack spacing="sm">
            <Select
              label={t("Password email template")}
              data={templateOptions(passwordTemplates)}
              value={form.values.passwordTemplateId}
              onChange={(id) => {
                if (id === BUILTIN_TEMPLATE) {
                  applyPasswordTemplate(null);
                  return;
                }
                applyPasswordTemplate(
                  passwordTemplates.find((doc) => doc._id === id) || null
                );
              }}
              disabled={loadingTemplates || sending}
              searchable
            />
            <TextInput
              label={t("Password email subject")}
              {...form.getInputProps("passwordSubject")}
              onFocus={() => setActiveField("passwordSubject")}
              disabled={sending}
            />
            <div onFocusCapture={() => setActiveField("passwordBody")}>
              <TextEditor
                key={passwordBodyKey}
                form={form}
                name="passwordBody"
                label={t("Password email body")}
              />
            </div>
          </Stack>
        )}
        <Alert icon={<IconPaperclip size={16} />} color="gray">
          <Stack spacing={6}>
            <Text size="sm">{t(config.attachHint)}</Text>
            {attachments.map((item) => (
              <Checkbox
                key={item.key}
                label={`${item.label}${item.filename ? ` (${item.filename})` : ""}`}
                checked={selectedKeys.includes(item.key)}
                disabled={!item.allowed || sending}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setSelectedKeys((current) => {
                    if (checked) {
                      return current.includes(item.key)
                        ? current
                        : [...current, item.key];
                    }
                    return current.filter((key) => key !== item.key);
                  });
                  setPreview(null);
                }}
              />
            ))}
          </Stack>
        </Alert>
        {entity === "debitNote" && entityId ? (
          <EntityEmailStoredFiles
            files={entityFiles}
            selectedIds={selectedEntityFileIds}
            disabled={sending}
            onChange={(ids) => {
              setSelectedEntityFileIds(ids);
              setPreview(null);
            }}
          />
        ) : null}
        {preview && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" weight={600} mb={4}>
              {t("Preview")}
            </Text>
            <Text size="sm" mb="xs">
              {preview.subject}
            </Text>
            <div dangerouslySetInnerHTML={{ __html: preview.body || "" }} />
            {form.values.encrypt && preview.passwordSubject ? (
              <>
                <Text size="sm" weight={600} mt="sm" mb={4}>
                  {t("Password email preview")}
                </Text>
                <Text size="sm" mb="xs">
                  {preview.passwordSubject}
                </Text>
                <div
                  dangerouslySetInnerHTML={{ __html: preview.passwordBody || "" }}
                />
              </>
            ) : null}
          </Paper>
        )}
        <Stack spacing={4}>
          <Text size="sm" weight={600}>
            {t("Email sent log")}
          </Text>
          {recentLog.length ? (
            recentLog.map((entry, index) => (
              <Text
                key={`${entry.sentAt || index}-${entry.to || ""}`}
                size="xs"
                color="dimmed"
              >
                {formatSentAt(entry.sentAt)} — {entry.to}
                {entry.cc ? ` (cc ${entry.cc})` : ""}
                {entry.bcc ? ` (bcc ${entry.bcc})` : ""} — {entry.subject}
                {String(entry.source || "").endsWith("_PASSWORD")
                  ? entry.status === "FAILED"
                    ? ` — ${t("password email failed")}`
                    : ` — ${t("password email sent")}`
                  : entry.encrypted
                    ? ` — ${t("encrypted")}`
                    : ""}
              </Text>
            ))
          ) : (
            <Text size="xs" color="dimmed">
              {t(config.emptyLog)}
            </Text>
          )}
        </Stack>
        <Group position="apart" mt="sm">
          <Group>
            <Popover
              width={280}
              position="top-start"
              withArrow
              shadow="md"
              opened={saveOpen}
              onChange={setSaveOpen}
            >
              <Popover.Target>
                <Button
                  variant="default"
                  leftIcon={<IconDeviceFloppy size={16} />}
                  disabled={sending}
                  onClick={() => setSaveOpen((open) => !open)}
                >
                  {t("Save as template")}
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack spacing="xs">
                  <TextInput
                    label={t("Template name")}
                    value={templateName}
                    onChange={(event) => setTemplateName(event.currentTarget.value)}
                    disabled={savingTemplate}
                  />
                  <Button
                    size="xs"
                    loading={savingTemplate}
                    onClick={async () => {
                      await saveTemplate({
                        name: templateName,
                        type: config.templateType,
                        subject: form.values.subject,
                        body: form.values.body,
                        cc: form.values.cc,
                        bcc: form.values.bcc,
                        apply: (tpl) => {
                          applyDocumentTemplate(tpl);
                          setSaveOpen(false);
                          setTemplateName("");
                        },
                      });
                    }}
                  >
                    {t("Save")}
                  </Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
            {form.values.encrypt && (
              <Popover
                width={280}
                position="top-start"
                withArrow
                shadow="md"
                opened={savePasswordOpen}
                onChange={setSavePasswordOpen}
              >
                <Popover.Target>
                  <Button
                    variant="default"
                    leftIcon={<IconDeviceFloppy size={16} />}
                    disabled={sending}
                    onClick={() => setSavePasswordOpen((open) => !open)}
                  >
                    {t("Save password template")}
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack spacing="xs">
                    <TextInput
                      label={t("Template name")}
                      value={passwordTemplateName}
                      onChange={(event) =>
                        setPasswordTemplateName(event.currentTarget.value)
                      }
                      disabled={savingTemplate}
                    />
                    <Button
                      size="xs"
                      loading={savingTemplate}
                      onClick={async () => {
                        await saveTemplate({
                          name: passwordTemplateName,
                          type: config.passwordTemplateType,
                          subject: form.values.passwordSubject,
                          body: form.values.passwordBody,
                          cc: form.values.cc,
                          bcc: form.values.bcc,
                          apply: (tpl) => {
                            applyPasswordTemplate(tpl);
                            setSavePasswordOpen(false);
                            setPasswordTemplateName("");
                          },
                        });
                      }}
                    >
                      {t("Save")}
                    </Button>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            )}
          </Group>
          <Group>
            <Button variant="default" onClick={onClose} disabled={sending}>
              {t("Cancel")}
            </Button>
            <Button
              variant="default"
              onClick={handlePreview}
              loading={previewing}
              disabled={sending}
            >
              {t("Preview")}
            </Button>
            <Button onClick={handleSend} loading={sending}>
              {t("Send")}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
